// @ts-nocheck

import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../../client';
import ScrollShadow from '../../../components/ScrollShadow';
import { getRedirectUrl, RedirectServices } from '../../../config/urls';
import { getLang, __ } from '../../../i18n/l10n';
import { Locales } from '../../../types/common';
import { GiftRaceImgByLang, GRPrimaryButtonBackroundImgPath, GRHoux } from '../assets/img';
import { GREnrollmentBackgroundImgPath } from '../assets/img/backgrounds';
import { GRGlobalStatuses, type GRGlobalStatus } from '../types/global';

import GRViewWrapper from './common/GRViewWrapper';

const sharedMarginStyle = css`
  margin-bottom: 1.3em;
`;

const StyledGRViewWrapper = styled(GRViewWrapper)`
  padding: 2.5em;
  text-align: center;
`;

const GRLogo = styled.img`
  max-width: 100%;
  max-height: 20%;
  margin-bottom: 1em;
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2em;
  max-height: 80%;

  background-color: #11111177;

  text-align: center;
  font-family: 'BettingTroika', sans-serif;
`;

const Rules = styled(ScrollShadow)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  margin-bottom: 1em;
`;

const Rule = styled.div`
  ${sharedMarginStyle}
`;

const RuleTitle = styled.div`
  font-weight: bold;
  margin-bottom: 0.3em;
  color: #fad051;
  font-size: 1.3em;
`;

const RuleDescription = styled.div`
  line-height: 1.4em;
  font-family: 'BettingTradeGothicLTStd';
  font-weight: bold;
`;

const FullRulesLink = styled.div`
  cursor: pointer;
  color: white;
  text-decoration: underline;
  text-underline-offset: 3px;
`;

const StartButton = styled.div`
  padding: 2em 4.5em !important;

  transition: none 150ms ease-in-out;
  transition-property: transform, filter;

  background-image: url(${GRPrimaryButtonBackroundImgPath});
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center center;
  user-select: none;

  ${({ $disabled }) => css`
    ${$disabled
      ? css`
          filter: grayscale(1);
          pointer-events: none;
        `
      : css`
          &:active {
            transform: scale(1.05);
          }

          ${!Target.__MOBILE__
            ? css`
                cursor: pointer;

                &:hover {
                  filter: brightness(110%);
                }
              `
            : undefined}
        `}
  `}
`;

const PhaseDescriptionList = styled.ul`
  list-style: none;
  color: #cf63cf;
  font-family: 'BettingTradeGothicLTStd';
  font-weight: bold;
  font-style: italic;
`;

const PhaseDescription = styled.li`
  &:not(:last-child) {
    margin-bottom: 0.25em;
  }

  &:before {
    content: '';
    display: inline-block;
    height: 1.3em;
    width: 1.3em;
    margin-right: 0.3em;
    transform: translateY(0.25em) rotateY(180deg);
    background-image: url(${GRHoux});
    background-repeat: no-repeat;
    background-size: contain;
  }
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  z-index: -1;

  background-image: url(${GREnrollmentBackgroundImgPath});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
`;

type GREnrollmentProps = {
  className?: string;
  openExternalLink: (...params: unknown) => unknown;
  onEnroll: () => void;
  globalStatus: GRGlobalStatus;
};

const GREnrollment = ({ className, openExternalLink, onEnroll, globalStatus }: GREnrollmentProps) => {
  const onFullRulesClick = useCallback(() => {
    openExternalLink(getRedirectUrl(RedirectServices.GR_ALL_RULES), { auth: true });
  }, [openExternalLink]);

  return (
    <StyledGRViewWrapper className={className}>
      <Background />
      <GRLogo src={GiftRaceImgByLang[getLang() ?? Locales.EN]} />
      <InnerWrapper>
        <Rules>
          <Rule>
            <RuleTitle>{__('Game objective')}</RuleTitle>
            <RuleDescription>
              {__(
                'Complete all 3 phases of the game by placing winning bets and walk away with legendary gifts and lots of Freebets:',
              )}
            </RuleDescription>
            <PhaseDescriptionList>
              <PhaseDescription>{__('The Pegasus shoes to fly over your next match')}</PhaseDescription>
              <PhaseDescription>
                {__('The remote-controlled ball with its unpredictable trajectories')}
              </PhaseDescription>
              <PhaseDescription>{__('The chameleon jersey to change teams in a flash')}</PhaseDescription>
            </PhaseDescriptionList>
          </Rule>
          <Rule>
            <RuleTitle>{__('Prizepool')}</RuleTitle>
            <RuleDescription>{__('Win up to %1$s times your reference bet in Freebets!', 100)}</RuleDescription>
          </Rule>

          <FullRulesLink onClick={onFullRulesClick}>{__('Check the full rules')}</FullRulesLink>
        </Rules>
        <StartButton $disabled={globalStatus === GRGlobalStatuses.ENDING} onClick={onEnroll}>
          {__('!action:GiftRace Start')}
        </StartButton>
      </InnerWrapper>
    </StyledGRViewWrapper>
  );
};

export default GREnrollment;

// __('Complete all 3 phases of the game by placing winning bets and walk away with legendary gifts and lots of Freebets:')
